import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang, formatApiError } from "../lib/providers";
import { Receipt, Translate, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
  const { t, lang, toggleLang } = useLang();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[var(--brand)] flex items-center justify-center">
              <Receipt weight="fill" size={14} color="white" />
            </div>
            <span className="font-heading font-semibold">FalkeDeck</span>
          </div>
          <button onClick={toggleLang} data-testid="forgot-lang-switch"
            className="flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
            <Translate size={14} />
            <span className="label-overline">{lang.toUpperCase()}</span>
          </button>
        </div>

        <div className="label-overline mb-2">{t("forgotPassword")}</div>
        <h2 className="font-heading text-3xl font-semibold tracking-tight text-[var(--ink)]">
          {t("forgotTitle")}
        </h2>
        <p className="text-[var(--muted)] mt-2 text-sm">{t("forgotSubtitle")}</p>

        {submitted ? (
          <div className="mt-8" data-testid="forgot-success">
            <div className="border border-[var(--border)] bg-[var(--surface)] p-5 rounded-sm flex gap-3">
              <EnvelopeSimple size={20} className="text-[var(--brand)] mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--ink)] leading-relaxed">{t("forgotSent")}</div>
            </div>
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-[var(--brand)] hover:underline"
            >
              <ArrowLeft size={14} /> {t("backToLogin")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="label-overline block mb-1.5">{t("email")}</label>
              <input
                data-testid="forgot-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm text-[var(--ink)] focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-soft)] focus:outline-none"
              />
            </div>
            {error && (
              <div data-testid="forgot-error" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}
            <button
              data-testid="forgot-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white font-medium text-sm rounded-sm transition-colors disabled:opacity-60"
            >
              {loading ? "…" : t("forgotSubmit")}
            </button>
            <Link
              to="/login"
              data-testid="forgot-cancel"
              className="block text-center text-sm text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
            >
              <ArrowLeft size={14} className="inline mr-1" />
              {t("backToLogin")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
